import { Grid, Button } from '@mui/material';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import UserContext from '../store/User/UserContext';
import { useLocationsByLoggedInUserRole, useCookieLocation, useWorktypes } from '../hooks';
import Container from '../components/Layout/Container';
import MultiSelectWorktypeLocDateRangeFilter from '../components/General/MultiSelectWorktypeLocDateRangeFilter';
import EventReductionTable from '../components/Events/EventReductionTable';
import { useDeleteEvent, useEvents, getEventsQueryKey, usePostEvent } from '../hooks/api/events';
import EventReductionDialog from '../components/Events/EventReductionDialog';
import EventReductionExportButton from '../components/Events/EventReductionExportButton';
import { queryClient } from '../services/queryClient';
import { getEventById } from '../services/event';
import GridContainer from '../components/Layout/GridContainer';
import { useSocket } from '../store/SocketContext';
import { SocketEvents } from '../enums/SocketEvents';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import dayjs from '@utils/dayjs';

const Events = () => {
    const { t } = useTranslation('allDeltas');
    const socket = useSocket();
    const loggedInUser = useContext(UserContext);
    // TEF aliases to WFM in HEMTService, so isWFMUser is true for TEF users.
    const canWrite = loggedInUser?.isWFMUser || loggedInUser?.isOSPUser;
    const seesAllLocations = loggedInUser?.isWFMUser || loggedInUser?.isWTMUser;

    const [locationOptions] = useLocationsByLoggedInUserRole({
        prependAllLocationsOption: seesAllLocations,
    });
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedLocations, setSelectedLocations] = useExpireLocalStorage('events-selected-locations', []);
    const [worktypeOptions] = useWorktypes({
        prependAllWorktypesOption: true,
        location: loggedInUser?.isOSPUser ? selectedLocation : undefined,
    });
    const [selectedWorktypes, setSelectedWorktypes] = useExpireLocalStorage(
        'events-selected-worktypes',
        worktypeOptions.length > 0 ? [worktypeOptions[0]] : []
    );
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs().add(6, 'months'));

    const [selectedRow, setSelectedRow] = useState(null);

    const [showEventReductionDialog, setShowEventReductionDialog] = useState(false);
    const filterParams = useMemo(
        () => ({
            startDate,
            endDate,
            location: loggedInUser?.isOSPUser ? selectedLocation : selectedLocations.map((loc) => loc.id),
            worktype: selectedWorktypes.map((worktype) => worktype.id),
        }),
        [startDate, endDate, selectedLocation, selectedLocations, selectedWorktypes, loggedInUser]
    );
    const { events, durationTotal, reductionTotal } = useEvents(filterParams);
    const { mutateAsync: handleDeleteEvent } = useDeleteEvent(filterParams);
    const { mutate: handlePostEvent, isLoading: isPostEventLoading } = usePostEvent({
        filterParams,
        onSuccess: () => {
            setShowEventReductionDialog(false);
            setSelectedRow(null);
        },
    });

    useEffect(() => {
        if (locationOptions?.length && selectedLocations.length === 0) {
            setSelectedLocations([locationOptions[0]]);
        }
    }, [locationOptions, selectedLocations, setSelectedLocations]);

    useEffect(() => {
        if (worktypeOptions?.length && selectedWorktypes.length === 0) {
            setSelectedWorktypes([worktypeOptions[0]]);
        }
    }, [worktypeOptions, selectedWorktypes, setSelectedWorktypes]);

    useEffect(() => {
        const onPostedEvent = () => {
            queryClient.invalidateQueries(getEventsQueryKey(filterParams));
        };
        socket.on(SocketEvents.postedEvent, onPostedEvent);

        return () => {
            socket.off(SocketEvents.postedEvent, onPostedEvent);
        };
    }, [filterParams, socket]);
    return (
        <Container>
            <GridContainer>
                <MultiSelectWorktypeLocDateRangeFilter
                    locationOptions={locationOptions}
                    selectedLocation={selectedLocation}
                    onSelectedLocationChange={setSelectedLocation}
                    selectedLocations={selectedLocations}
                    onSelectedLocationsChange={setSelectedLocations}
                    worktypeOptions={worktypeOptions}
                    selectedWorktypes={selectedWorktypes}
                    onSelectedWorktypesChange={setSelectedWorktypes}
                    startDate={startDate}
                    onStartDateChange={setStartDate}
                    endDate={endDate}
                    onEndDateChange={setEndDate}
                ></MultiSelectWorktypeLocDateRangeFilter>
                {canWrite && (
                    <Grid item>
                        <Button variant="contained" onClick={() => setShowEventReductionDialog(true)}>
                            <AddIcon />
                        </Button>
                        {showEventReductionDialog && (
                            <EventReductionDialog
                                dialogTitle={t('event.newEvent')}
                                showDialog={showEventReductionDialog}
                                onShowDialog={setShowEventReductionDialog}
                                record={
                                    selectedRow
                                        ? {
                                              ...selectedRow,
                                              serieId: selectedRow?.eventSerieId,
                                              serie: selectedRow?.serie,
                                          }
                                        : null
                                }
                                onRowSelect={setSelectedRow}
                                location={seesAllLocations ? loggedInUser.primaryLocationId : selectedLocation}
                                postRecord={handlePostEvent}
                                isLoading={isPostEventLoading}
                            />
                        )}
                    </Grid>
                )}
                {seesAllLocations && (
                    <>
                        <Grid item>
                            <EventReductionExportButton
                                recordType="event"
                                rows={events}
                                start={startDate}
                                end={endDate}
                                pinnedBottomRowData={[
                                    {
                                        worktypeName: t('common.total'),
                                        duration: durationTotal,
                                        reduction: reductionTotal,
                                        id: '',
                                    },
                                ]}
                            />
                        </Grid>
                    </>
                )}
                <EventReductionTable
                    tableRows={events}
                    durationTotal={durationTotal}
                    reductionTotal={reductionTotal}
                    onRowEditConfirmation={() => setShowEventReductionDialog(true)}
                    onRowCopy={() => setShowEventReductionDialog(true)}
                    selectedRow={selectedRow}
                    onRowSelect={(row) => setSelectedRow(row)}
                    onRowDelete={handleDeleteEvent}
                    getRecordById={getEventById}
                    canWrite={canWrite}
                ></EventReductionTable>
            </GridContainer>
        </Container>
    );
};

export default Events;
